/**
 * The English UI catalog.
 *
 * There is deliberately no locale switch yet. Keeping every visible string and
 * formatter behind this typed object is the cheap part to do now; locale
 * negotiation, translated catalogs, and content translation can remain
 * separate decisions.
 */
export const messages = {
  common: {
    brand: "MedQAIR",
    loading: "Loading…",
    yourTraining: "Your training",
    complete: "Complete",
    overdue: "Overdue",
    inProgress: "In progress",
    notStarted: "Not started",
    notApplicable: "Not applicable, excluded from the totals",
    day: (count: number) => (count === 1 ? "day" : "days"),
  },
  app: {
    profileLoadError: "Could not load your profile.",
    retryProfile: "Try again",
  },
  preview: {
    title: "Pull request preview",
    warning: "Synthetic people and evidence only. Nothing here is a valid training record.",
  },
  signIn: {
    title: "Training",
    intro: "Read the procedure and answer a few questions. Passing records the evidence for you.",
    continueGoogle: "Continue with Google",
    googleUnavailable: "Google sign-in is not configured.",
    localDivider: "or, for local development",
    previewDivider: "or, with a synthetic preview identity",
    previewAccounts:
      "Use reviewer@example.invalid for all three organisations, or complete, progress, overdue, exempt, or superseded @example.invalid.",
    email: "Email",
    emailPlaceholder: "you@medqair.com",
    signingIn: "Signing in",
    localSignIn: "Sign in without Google",
    unknownUser:
      "That address is not registered. An administrator has to add you before you can sign in.",
    unknownUserLocal: "That address is not registered. An administrator has to add you first.",
    noMembership: "Your account is not an active member of any organization.",
    emailUnverified: "That Google account's email address is not verified.",
    sessionExpired: "That sign-in attempt expired. Please try again.",
    failed: "Sign-in did not complete. Please try again.",
    providerUnavailable: "Google could not be reached. Please try again in a moment.",
    couldNotSignIn: "Could not sign in.",
    optionsUnavailable:
      "Sign-in options could not be loaded. The service may be busy, so please try again in a moment.",
    retryOptions: "Try again",
  },
  appBar: {
    productName: "Training",
    accountMenu: "Account menu",
  },
  account: {
    organization: "Organisation",
    switch: "Switch",
    switching: "Switching…",
    switchError: "Could not switch organisation.",
    alsoMemberOf: (names: string[]) =>
      `Also a member of ${names.length === 1 ? names[0] : `${names.slice(0, -1).join(", ")} and ${names.at(-1)}`}.`,
    onlyMembership: "This is your only organisation.",
    profile: "Profile and preferences",
    record: "Your training record",
    administration: "Administration",
    compliance: "Training compliance",
    help: "Help and support",
    signOut: "Sign out",
  },
  /**
   * One address for now. A mailto keeps support reachable without a ticketing
   * surface to build, staff, and hold evidence in.
   */
  support: {
    address: "training@medqair.com",
    mailto: "mailto:training@medqair.com?subject=MedQAIR%20Training",
  },
  profile: {
    title: "Profile and preferences",
    languageAndTime: "Language and time",
    timezone: "Time zone",
    timezoneHelp:
      "Due dates and reminder days use this local calendar. Changing it does not move a due date you have already been given.",
    timezoneSave: "Save",
    timezoneCancel: "Cancel",
    timezoneSaving: "Saving…",
    timezoneSaved: "Saved.",
    timezoneSaveError: "Could not save your time zone.",
    language: "Interface language",
    languageValue: "English (United Kingdom)",
    languageHelp:
      "The interface is English until translated catalogs exist. Procedures are shown in the language they were approved in and are never machine translated.",
    email: "Email",
    requiredReminders: "Reminders for required training",
    requiredRemindersHelp: "Sent before a due date, then again while it stays outstanding.",
    alwaysOn: "Always on",
    requiredRemindersRule:
      "Required training is chased whether or not you want it to be, because the organisation has to show it tried. There is nothing to switch off here.",
    security: "Security",
    signedInWith: (provider: string) =>
      provider === "google" ? "Signed in with Google" : `Signed in with ${provider}`,
    signedInHelp: "Two-step verification is managed by that account, not here.",
    accountSection: "Account",
    identityHelp:
      "Your name and address come from your sign-in account and cannot be edited here. A training record cites them, so an administrator has to make the correction.",
    memberships: "Memberships",
    roleAdministrator: "Administrator",
    roleLearner: "Learner",
    currentMembership: "Current",
    yourData: "Your data",
    retention:
      "Training evidence is kept for the retention period in DOC-1 Document and Record Control and cannot be deleted on request while it is required. Ask your administrator about anything else held about you.",
    legal: "Legal and support",
    contactSupport: "Contact support",
  },
  home: {
    title: "Your training",
    summary: "Summary",
    assigned: "Assigned",
    assignedTraining: "Assigned training",
    doTheseFirst: "Do these first",
    completeGroup: "Complete",
    nothingAssigned: "Nothing is assigned to you right now.",
    nothingOutstanding: "Nothing is outstanding. Everything assigned to you is complete.",
    loadError: "Could not load your training.",
    dueToday: "Due today",
    overdueBy: (days: number) => `Overdue by ${days} ${messages.common.day(days)}`,
    overdueDays: (days: number) => `Overdue ${days} ${messages.common.day(days)}`,
    dueIn: (days: number) => `Due in ${days} ${messages.common.day(days)}`,
    documentsRead: (read: number, total: number) =>
      `${read} of ${total} ${total === 1 ? "document" : "documents"} read`,
    quizPassed: "quiz passed",
    completedWithApplicableQuiz: "Completed using the applicable quiz at that time.",
    completedOn: (date: string) => `Completed on ${date} using the applicable quiz at that time.`,
    superseded: "Superseded before completion",
    cancelled: "Cancelled before completion",
    quizNotStarted: "Quiz not started",
    subtitle: (orgName: string) => orgName,
    views: "Training views",
    current: "Current",
    history: "History",
    optionalLibrary: "Optional library",
    optional: "Optional",
    noHistory: "No completed training history is available.",
    noOptional: "No optional training is available.",
    libraryLoadError: "Could not load the optional training library.",
    assignError: "Could not add optional training.",
    selfAssign: "Add to my training",
    assigning: "Adding…",
    assignedOptional: "Added",
    unsubscribe: "Remove from my training",
    unsubscribing: "Removing…",
    unsubscribeError: "Could not remove optional training.",
    revision: (revision: string) => `Revision ${revision}`,
  },
  module: {
    loadError: "Could not load this training.",
    openError: "Could not open the questions. Try again in a moment.",
    submitError: "Could not submit your answers. Start a new attempt.",
    documents: "Documents",
    revision: (revision: string) => `Revision ${revision}`,
    metaRevision: "Revision",
    metaDue: "Due",
    metaQuiz: "Quiz",
    quizSummary: (questions: number, passMark: number) =>
      `${questions} questions, pass at ${passMark}%`,
    whatChanged: "What changed",
    fullProcedure: "Full procedure",
    viewLabel: "Document view",
    editsBetween: (count: number, from: string, to: string) =>
      `${count} ${count === 1 ? "edit" : "edits"} between revision ${from} and ${to}`,
    previousEdit: "Previous edit",
    nextEdit: "Next edit",
    mobileNavigation: "Document navigation",
    backToTop: "Back to top",
    goToQuestions: "Go to questions",
    viewCompletion: "View completion",
    readOnly: "View record",
    superseded: "This training was superseded before completion.",
    cancelled: "This training was cancelled before completion.",
    legendAdded: "Added",
    legendRemoved: "Removed",
    comparison: "Revision changes",
    acknowledged: "Acknowledged",
    revisionReason: "Why this revision was issued",
    openTable: "Open full-width table",
    tableView: "Full-width table",
    closeTable: "Close table",
    document: "Document",
    documentRevision: (documentKey: string, revision: string) =>
      `${documentKey} revision ${revision}`,
    digest: "Digest",
    /** The signed PDF rendition, shown page by page beside the questions. */
    pdfRegion: (documentKey: string, revision: string) =>
      `${documentKey} revision ${revision}, signed PDF`,
    pdfNotice: "This is the signed document, shown exactly as approved.",
    pdfLoading: "Loading the signed document…",
    pdfError:
      "The signed document could not be shown here. Open it in a new tab to read it in full.",
    openFullDocument: "Open the full document",
    /** The immutable signed PDF published beside the accessible Markdown. */
    viewSignedOriginal: "View signed original",
    signedOriginalNotice:
      "The accessible text above is the approved procedure. The exact signed document it was produced from is also available.",
    pdfPages: (count: number) => `${count} ${count === 1 ? "page" : "pages"}`,
    pdfPage: (page: number, count: number) => `Page ${page} of ${count}`,
    quiz: "Quiz",
    comprehension: "Comprehension check",
    quizPolicy: (questions: number, passMark: number) =>
      `${questions} questions, and you need ${passMark}% to pass. The procedure stays open beside them, so look things up. Passing records that you have read and understood it.`,
    tryAgain: "Try again",
    start: "Start the questions",
    submit: "Submit answers",
    passed: "Passed",
    notPassed: "Not passed",
    recorded: "This training is recorded as complete.",
    retry: (passMark: number) => `You need ${passMark}%. Look the answers up and try again.`,
    attempts: (count: number) =>
      `${count} previous ${count === 1 ? "attempt" : "attempts"} recorded.`,
  },
  admin: {
    overview: "Overview",
    peopleAndCurricula: "People and curricula",
    materials: "Materials",
    automation: "Automation",
    evidenceAndHistory: "Evidence and history",
    detailSummary: (required: number, optional: number, historical: number) =>
      `${required} current required · ${optional} optional · ${historical} historical`,
    completedOptional: "Completed optional training",
    completedRequired: "Completed required training",
    supersededIncomplete: "Superseded before completion",
    cancelled: "Cancelled",
    trainingOutstanding: "Training outstanding",
    completedAt: (date: string) => `Completed ${date}`,
    preservedQuizEvidence: "Completed before the quiz change; original evidence retained.",
    loadError: "Could not load compliance. You may not administer this organization.",
    title: "Training compliance",
    /** The header band states a figure, so it needs the noun, not the caveat. */
    notApplicableShort: "Not applicable",
    tabWho: "Who has done what",
    tabGovernance: "Governance",
    tabRecords: "Records",
    tabReminders: "Reminders",
    panels: "Compliance panels",
    orgSummary: (people: number, modules: number) =>
      `${people} ${people === 1 ? "person" : "people"} · ${modules} ${modules === 1 ? "module" : "modules"}`,
    selectedPerson: "Selected person",
    closePerson: "Close the selected person",
    outstanding: "Outstanding",
    nothingOutstandingForPerson: "Nothing outstanding.",
    auditNote:
      "Every decision here is appended to the audit trail with who made it and why. Nothing is edited or removed.",
    deferredState: "Deferred",
    /** Never colour alone: this is the cell's whole meaning, in words. */
    cellLabel: (person: string, module: string, state: string) => `${person}, ${module}: ${state}`,
    matrixHelp:
      "Columns are training modules, labelled with their document keys where available. Move between cells with the arrow keys, and press Enter to inspect that person and module.",
    personTally: (complete: number, required: number) => `${complete} of ${required} complete`,
    summary: "Summary",
    completion: "Completion",
    people: "People",
    complianceMatrix: "Compliance matrix",
    matrixTitle: "Who has done what",
    findPerson: "Find a person",
    findPersonPlaceholder: "Name, email or role",
    filterModule: "SOP or module",
    allModules: "All SOPs and modules",
    filterStatus: "Training status",
    allStatuses: "All statuses",
    outstandingIncludingDeferred: "Outstanding (including deferred)",
    resetFilters: "Reset filters",
    matrixFilteredCount: (
      people: number,
      totalPeople: number,
      modules: number,
      totalModules: number,
    ) => `Showing ${people} of ${totalPeople} people and ${modules} of ${totalModules} modules.`,
    matrixStatusScope: "People matching the selected training status.",
    matrixApplicabilityNote: "Not assigned means applicability still needs checking.",
    matrixNoMatches: "No people match these filters.",
    doneAllTraining: "Done (all training)",
    selectedTraining: "Selected training",
    selectedTrainingUnassigned:
      "No assignment is shown. This does not establish that training is unnecessary.",
    person: "Person",
    done: "Done",
    notAssigned: "Not assigned",
    overdueSection: "Overdue",
    nobodyOverdue: "Nobody is overdue.",
    trainingRecords: "Training records",
    trainingRecordsHelp:
      "Download the record as it stood at the end of the selected date in your profile timezone. The PDF is the auditor handoff; Markdown remains the primary docs-as-code record.",
    recordAsOf: "Record as at",
    recordDateRequired: "Choose today or an earlier date.",
    recordLoadError: "Could not load training records for that date.",
    noTrainingRecords: "No active curriculum is available to export.",
    downloadRecord: (format: "pdf" | "markdown" | "csv") =>
      `Download ${format === "markdown" ? "Markdown" : format.toUpperCase()}`,
    deliveryIssues: "Delivery issues",
    deliveryPrivacy: "Mail delivery facts only. Opens and clicks are not collected.",
    noDeliveryIssues: "No reminder delivery issues have been reported.",
    adminDigest: "Administrator digest",
    learnerReminder: "Learner reminder",
    bounced: "Bounced",
    deferred: "Deferred",
  },
  /**
   * Training governance.
   *
   * Every dialog states its rule before it asks for anything, because the two
   * decisions an administrator can confuse are exactly the two that quietly
   * change what compliance means: moving a due date keeps the requirement, and
   * non-applicability removes it. Where a dialog says a rationale is kept, it
   * names the place that actually keeps it.
   */
  governance: {
    heading: "Training governance",
    intro:
      "Curricula decide who is expected to train. Every decision here is appended to the audit trail with your name and the time, and nothing already recorded is edited or removed.",
    coverageGaps: (count: number) =>
      `${count} active ${count === 1 ? "person has" : "people have"} no curriculum`,
    awaitingCurriculum: "Awaiting curriculum during onboarding",
    saved: "Decision saved and added to the audit trail.",
    saveError: (detail: string) => `Could not save: ${detail}`,
    saveFailed: "Could not save.",

    curriculumContents: "Curriculum contents",
    curriculumContentsHelp:
      "Inspect the exact modules and document revisions before assigning a curriculum.",
    noCurricula: "No active curricula are published.",
    curriculumSummary: (participants: number, modules: number, documents: number) =>
      `${participants} participants, ${modules} modules, ${documents} documents`,
    emptyCurriculum: "This curriculum contains no modules.",

    people: "People and curricula",
    peopleHelp: "Current curriculum access for every active person in this organization.",
    noCurriculumAssigned: "No curriculum assigned",

    outstanding: "Outstanding training",
    outstandingHelp:
      "Everything still required from somebody. Change a due date or record non-applicability on the row it concerns.",
    nothingOutstanding: "Nothing is outstanding.",
    dueOn: (date: string) => `Due ${date}`,

    cycles: "Training cycles",
    cyclesHelp:
      "The periods this organisation's training is run in. Starting one does not change who is expected to train.",
    noCycles: "No cycle has been started yet.",
    cyclePeriod: (startsOn: string, dueOn: string, participants: number) =>
      `${startsOn} to ${dueOn}, ${participants} participants`,

    assign: "Assign curriculum",
    startCycle: "Start cycle",
    changeDueDate: "Change due date",
    recordNonApplicability: "Record non-applicability",
    /**
     * A row action says what it does; its accessible name also says what it
     * does it to, because a screen reader hears twelve identical buttons
     * otherwise.
     */
    actionFor: (action: string, subject: string) => `${action}: ${subject}`,
    actionForObligation: (action: string, person: string, module: string) =>
      `${action}: ${person}, ${module}`,
    close: "Close",
    cancel: "Cancel",
    saving: "Saving…",

    assignTitle: "Assign a curriculum",
    assignRule:
      "A curriculum decides which training this person is expected to complete. Assigning one adds the training it contains and does not change anything they have already completed.",
    forPerson: (name: string) => `For ${name}`,
    curriculum: "Curriculum",
    chooseCurriculum: "Choose curriculum",
    assignReason: "Why this curriculum applies (optional)",
    /**
     * Optional on purpose, and the API agrees: see the decision in cd50625.
     * Assigning a curriculum adds an obligation rather than weakening one, so it
     * is not a decision that has to be justified before it can be taken. The
     * rationale requirement belongs to the two actions that reduce what somebody
     * owes, which is deferral and non-applicability.
     */
    assignReasonHelp:
      "Optional. If you give a reason it is written to the audit trail with your name, and cannot be edited or removed later.",
    assignReasonPlaceholder: "Why this person is expected to complete this training",
    assignSubmit: "Assign",

    cycleTitle: "Start an annual cycle",
    cycleRule:
      "A cycle names the period this curriculum is trained in and sets the date it falls due. It does not change who is expected to train, and it does not clear anybody's outstanding training.",
    forCurriculum: (label: string) => `For ${label}`,
    cycleName: "Cycle name",
    cycleNamePlaceholder: "Cycle name, for example QMS 2026",
    cycleNameHelp:
      "The name is recorded on the cycle and in the audit trail with your name, and it is how this period is reported later.",
    cycleStarts: "Starts",
    cycleDue: "Due",
    cycleSubmit: "Create annual cycle",

    deferralTitle: "Defer or correct a due date",
    deferralRule:
      "Moving a due date does not remove the requirement. This person still has to complete this training and it stays visible in compliance. Only the date moves.",
    forObligation: (person: string, module: string) => `${person}: ${module}`,
    currentDue: (date: string) => `Currently due ${date}`,
    newDueDate: "New due date",
    rationale: "Rationale",
    deferralRationaleHelp:
      "Say why the date is moving, in operational terms. This rationale appears in the exported training record and cannot be edited or removed, so leave out medical detail.",
    deferralRationalePlaceholder: "Brief operational rationale, without medical details",
    deferralSubmit: "Defer due date",
    correctionSubmit: "Correct due date",
    revokeRule:
      "Revoking puts the original due date back. It is recorded as a new decision, and the decision it replaces stays on the record.",
    revokeSubmit: "Revoke",

    exemptionTitle: "Record non-applicability",
    exemptionRule:
      "Not applicable is only for training that genuinely does not apply to this person's role. It is not a way to clear a late completion, and it is not for sickness or leave: move the due date instead.",
    exemptionRationaleHelp:
      "Say why this training does not apply to this person's role. This rationale appears in the exported training record and cannot be edited or removed.",
    exemptionRationalePlaceholder: "Why this training does not apply to this person's role",
    exemptionSubmit: "Record exemption",
  },
} as const;
